/* eslint-disable @typescript-eslint/no-explicit-any */
import { Chart as ChartJS, registerables } from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-plugin-dragdata";
import { useSetAtom } from "jotai";
import { updateContourValuesAtom } from "atoms";
import { presetPitchValues } from "shared/models";
import { ContourLineGraphColors } from "shared/constants";

ChartJS.register(...registerables);

interface ContourLineChartProps {
  inputData: { x: number; y: number }[];
  onChange?: () => void;
}

export default function ContourLineChart({
  inputData,
  onChange,
}: ContourLineChartProps) {
  const updateContourValues = useSetAtom(updateContourValuesAtom);

  const handleDragEnd = (e: any, _dataIndex: any, index: any, value: any) => {
    e.target.style.cursor = "default";
    updateContourValues({
      index,
      data: { time: value.x, value: value.y },
    });

    if (onChange) {
      onChange();
    }
  };

  const config = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        cornerRadius: 0,
        padding: 16,
        callbacks: {
          title: () => "", // Return an empty string to remove the title
          label: (context: any) => {
            const xValue = context.parsed.x;
            const yValue = context.parsed.y;
            return [`Position: ${xValue}%`, `Frequency: ${yValue} st`]; // Display current x and y values
          },
        },
        bodyFont: {
          weight: 500,
          size: 14,
          family: "IBM Plex Sans",
        },
      },
      dragData: {
        round: 2,
        dragX: true,
        showTooltip: true,
        // onDragStart: () => {},
        onDrag: (e: any) => {
          e.target.style.cursor = "grabbing";
        },
        onDragEnd: handleDragEnd,
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: 100,
        ticks: {
          font: {
            size: 14,
          },
          callback(value: number) {
            return [0, 20, 40, 60, 80, 100].includes(value)
              ? `${value}%`
              : undefined; // Display the value as a percentage
          },
        },
        grid: {
          borderDash: [5, 5],
          drawBorder: false,
        },
      },
      y: {
        min: -4,
        max: 4,
        ticks: {
          font: {
            size: 14,
          },
          callback(value: number) {
            return [-4, -2, 0, 2, 4].includes(value)
              ? presetPitchValues.find((pitch) => pitch.value === `${value}st`)
                  ?.label
              : undefined; // Display the value as a percentage
          },
        },
        grid: {
          borderDash: [3, 3],
          drawBorder: false,
        },
      },
    },
  };

  const data = {
    datasets: [
      {
        data: inputData,
        backgroundColor: "white",
        borderColor: ContourLineGraphColors.GraphLine,
        tension: 0.3,
        pointRadius: inputData.map((_, index) =>
          index === 0 || index === inputData.length - 1 ? 0 : 5
        ),
        pointHoverRadius: inputData.map((_, index) =>
          index === 0 || index === inputData.length - 1 ? 0 : 8
        ),
        pointBorderColor: ContourLineGraphColors.Points,
        pointHoverBorderColor: ContourLineGraphColors.Points,
        pointBorderWidth: 2,
        pointHoverBorderWidth: 2,
      },
    ],
  };

  return <Line data={data} options={config as any} />;
}
